/**
 * Эталонная сцена для замера рендера (C1): постоянно ~N зомби идут со всех краёв к штабу в центре, бьют его, умирают и
 * появляются снова; 5 героев на платформах и пушка стреляют, летят снаряды, рвутся взрывы, бьют молнии, сыплются
 * монеты и цифры урона. Не бой и не баланс — только нагрузка на рендер. Детерминирована по сиду.
 *
 * Открыть: `/?bench=150` (main.tsx). Раскладка — как у настоящего ядра (`createLayout`): штаб в центре поля.
 */
import type {
  BattleSnapshot,
  CoinDropState,
  EffectKind,
  EffectState,
  HeroId,
  HeroState,
  ProjectileKind,
  ProjectileState,
  Vec2,
  ZombieKind,
  ZombieState,
} from "@newfungame/contracts";
import { DEFAULT_FIELD } from "@newfungame/contracts";
import { createLayout } from "@newfungame/engine";
import { directionFromDelta } from "./iso";

const HEROES: readonly HeroId[] = ["gunner", "bomber", "sparky", "blaze", "frosty"];
const HERO_SHOT: Record<HeroId, ProjectileKind> = {
  gunner: "bullet",
  bomber: "bomb",
  sparky: "spark",
  blaze: "flame",
  frosty: "frost",
  radiation: "bomb",
  cryo: "frost",
  laser: "spark",
  plague: "bomb",
};
const KINDS: readonly ZombieKind[] = ["walker", "walker", "runner", "brute", "shield"];
const SPEED: Record<ZombieKind | "boss", number> = { walker: 22, runner: 40, brute: 14, shield: 18, boss: 10 };
const DIE_TICKS = 24;
const ATTACK_TICKS = 90;
const STOP_RADIUS = 1500;

export interface BenchStream {
  step(): BattleSnapshot;
  current(): BattleSnapshot;
}

interface Z extends ZombieState {
  /** Тик смены анимации. */
  since: number;
}

export function createBenchStream(zombieCount = 150, seed = 1): BenchStream {
  let s = seed >>> 0 || 1;
  const rnd = (): number => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
  const field = { ...DEFAULT_FIELD };
  const layout = createLayout(field);
  const hq = layout.hq;
  let frame = 0;
  let nextId = 1;
  let killed = 0;
  let coinsTotal = 0;
  const zombies: Z[] = [];
  let projectiles: (ProjectileState & { t: number; flight: number; target: number })[] = [];
  let effects: EffectState[] = [];
  let coins: CoinDropState[] = [];

  const edgePoint = (): Vec2 => {
    const half = field.width / 2;
    const r = rnd() * 2 * (field.width + field.depth);
    if (r < field.width) return { x: -half + r, y: 0 };
    if (r < field.width + field.depth) return { x: half, y: r - field.width };
    if (r < 2 * field.width + field.depth) return { x: half - (r - field.width - field.depth), y: field.depth };
    return { x: -half, y: r - 2 * field.width - field.depth };
  };

  const spawn = (pos: Vec2 = edgePoint()): void => {
    const kind = KINDS[Math.floor(rnd() * KINDS.length)]!;
    const hpMax = 100;
    zombies.push({
      id: nextId++,
      kind,
      bossId: null,
      pos,
      facing: "s",
      anim: "walk",
      animTick: Math.floor(rnd() * 30),
      hp: hpMax,
      hpMax,
      shield: kind === "shield" ? 30 : 0,
      statuses: [],
      since: frame,
    });
  };

  // стартовая расстановка: зомби уже по всему полю
  for (let i = 0; i < zombieCount; i++) {
    const p = edgePoint();
    const k = 0.15 + rnd() * 0.85;
    spawn({ x: Math.round(hq.x + (p.x - hq.x) * k), y: Math.round(hq.y + (p.y - hq.y) * k) });
  }

  const heroes: HeroState[] = HEROES.map((heroId, slot) => ({
    id: nextId++,
    heroId,
    slot,
    pos: { ...layout.platforms[slot]! },
    facing: "s",
    anim: "idle",
    animTick: 0,
    level: 10,
    stars: 2,
    targetId: null,
    skill: { skillId: `${heroId}_skill`, cooldownLeft: 0, cooldownTotal: 300, activeLeft: 0 },
  }));

  const effect = (kind: EffectKind, pos: Vec2, durationTicks: number, value = 0, to: Vec2 | null = null): void => {
    effects.push({ id: nextId++, kind, pos: { ...pos }, to, startTick: frame, durationTicks, value });
  };

  const alive = (): Z[] => zombies.filter((z) => z.anim !== "die");

  const shoot = (from: Vec2, owner: HeroId | "hq", kind: ProjectileKind): number | null => {
    const list = alive();
    if (list.length === 0) return null;
    const t = list[Math.floor(rnd() * list.length)]!;
    const flight = 8 + Math.floor(rnd() * 6);
    projectiles.push({
      id: nextId++,
      kind,
      pos: { ...from },
      from: { ...from },
      to: { ...t.pos },
      owner,
      t: 0,
      flight,
      target: t.id,
    });
    effect("muzzle", from, 6);
    return t.id;
  };

  const hit = (z: Z, dmg: number): void => {
    if (z.anim === "die") return;
    const crit = rnd() < 0.15;
    const d = crit ? dmg * 3 : dmg;
    effect(crit ? "critNumber" : "damageNumber", { x: z.pos.x, y: z.pos.y }, 24, d);
    z.hp = Math.max(0, z.hp - d);
    if (z.hp === 0) {
      z.anim = "die";
      z.animTick = 0;
      z.since = frame;
      z.statuses = [];
      killed++;
      coins.push({ id: nextId++, pos: { ...z.pos }, value: 5, startTick: frame, flightTicks: 30 });
      effect("coinBurst", z.pos, 12);
    }
  };

  const snapshot = (): BattleSnapshot => ({
    frame,
    field,
    wave: {
      number: 25,
      isBoss: false,
      phase: "fight",
      tick: frame,
      total: zombieCount + killed,
      spawned: zombieCount + killed,
      killed,
      progress: Math.min(1000, Math.trunc((killed * 1000) / (zombieCount + killed))),
      boss: null,
    },
    hq: {
      pos: { ...hq },
      hp: 800,
      hpMax: 1000,
      look: "intact",
      cannon: { facing: "n", firing: frame % 20 === 0, targetId: null },
    },
    platforms: layout.platforms.map((pos, slot) => ({
      slot,
      pos: { ...pos },
      unlocked: slot < 5,
      unlockWave: [1, 5, 10, 20, 30, 50][slot]!,
      heroId: HEROES[slot] ?? null,
    })),
    heroes: heroes.map((h) => ({ ...h, pos: { ...h.pos }, skill: { ...h.skill } })),
    zombies: zombies.map(({ since: _since, ...z }) => ({ ...z, pos: { ...z.pos }, statuses: [...z.statuses] })),
    projectiles: projectiles.map(({ t: _t, flight: _f, target: _g, ...p }) => ({ ...p, pos: { ...p.pos } })),
    effects: effects.map((e) => ({ ...e })),
    parcels: [],
    parcelOpened: null,
    coins: coins.map((c) => ({ ...c })),
    wallet: { coins: 48_600 + coinsTotal, earnedThisWave: coinsTotal },
    autoSkills: { unlocked: true, enabled: true },
    events: [],
  });

  let last = snapshot();

  const step = (): BattleSnapshot => {
    frame++;
    // зомби: идут к штабу, бьют, умирают и уступают место новым
    for (let i = zombies.length - 1; i >= 0; i--) {
      const z = zombies[i]!;
      z.animTick++;
      if (z.anim === "die") {
        if (frame - z.since >= DIE_TICKS) {
          zombies.splice(i, 1);
          spawn();
        }
        continue;
      }
      const dx = hq.x - z.pos.x;
      const dy = hq.y - z.pos.y;
      const dist = Math.hypot(dx, dy);
      if (dist > STOP_RADIUS) {
        const v = SPEED[z.kind] * (z.statuses.includes("frozen") ? 0 : z.statuses.includes("slowed") ? 0.5 : 1);
        z.pos = { x: Math.round(z.pos.x + (dx / dist) * v), y: Math.round(z.pos.y + (dy / dist) * v) };
        z.facing = directionFromDelta(dx, dy) ?? z.facing;
      } else if (z.anim === "walk") {
        z.anim = "attack";
        z.animTick = 0;
        z.since = frame;
      } else if (frame - z.since > ATTACK_TICKS) {
        hit(z, 1000);
      }
    }
    // герои и пушка
    for (const h of heroes) {
      h.animTick++;
      if ((frame + h.slot * 7) % 18 === 0) {
        h.targetId = shoot(h.pos, h.heroId, HERO_SHOT[h.heroId]);
        h.anim = "shoot";
        h.animTick = 0;
        const t = zombies.find((z) => z.id === h.targetId);
        if (t) h.facing = directionFromDelta(t.pos.x - h.pos.x, t.pos.y - h.pos.y) ?? h.facing;
      } else if (h.anim === "shoot" && h.animTick > 10) {
        h.anim = "idle";
        h.animTick = 0;
      }
    }
    if (frame % 20 === 0) shoot({ x: hq.x, y: hq.y }, "hq", "cannonball");
    // снаряды
    projectiles = projectiles.filter((p) => {
      p.t++;
      const k = p.t / p.flight;
      p.pos = { x: Math.round(p.from.x + (p.to.x - p.from.x) * k), y: Math.round(p.from.y + (p.to.y - p.from.y) * k) };
      if (p.t < p.flight) return true;
      const z = zombies.find((q) => q.id === p.target);
      effect(p.kind === "bomb" || p.kind === "cannonball" ? "explosion" : "hit", p.to, 12, p.kind === "bomb" ? 900 : 0);
      if (z) {
        if (p.kind === "frost") z.statuses = ["frozen"];
        if (p.kind === "flame") z.statuses = ["burning"];
        hit(z, 35);
      }
      return false;
    });
    // молния Sparky — цепочка к соседу
    if (frame % 45 === 0) {
      const list = alive();
      const a = list[Math.floor(rnd() * list.length)];
      const b = list[Math.floor(rnd() * list.length)];
      if (a && b) effect("lightning", a.pos, 10, 0, { ...b.pos });
    }
    effects = effects.filter((e) => frame - e.startTick < e.durationTicks);
    const landed = coins.filter((c) => frame - c.startTick >= c.flightTicks);
    for (const c of landed) coinsTotal += c.value;
    coins = coins.filter((c) => frame - c.startTick < c.flightTicks);
    last = snapshot();
    return last;
  };

  return { step, current: () => last };
}
